import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { LanguageContext } from "../contexts/LanguageContext";
import QuestionOption from "../components/QuestionOption";
import Spinner from "../components/Spinner";
import Footer from "../components/Footer";
import logo from "../assets/companyLogo.png";

const Question = () => {
  const { state } = useContext(LanguageContext);

  const [isLoading, setIsLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [currentPageQuestions, setCurrentPageQuestions] = useState();
  const [currentPage, setCurrentPage] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [questionNumber, setQuestionNumber] = useState(0);
  const [isSelecting, setIsSelecting] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);

  const timeLimit = location.state?.time_limit;

  const perPage = 1;
  const pageNumber = 1;

  let { id, tempCode } = useParams();

  const [total, setTotal] = useState(0);

  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    getQuestion(pageNumber, true);
  }, []);

  useEffect(() => {
    if (timeLimit !== undefined && timeLimit !== null) {
      setTimeLeft(timeLimit);
    }
  }, [timeLimit]);

  useEffect(() => {
    if (timeLeft === null) return;

    if (timeLeft <= 0) {
      setShowModal(true);
      return;
    }

    const timer = setTimeout(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft]);

  const minutes = String(Math.floor((timeLeft || 0) / 60)).padStart(2, "0");

  const seconds = String((timeLeft || 0) % 60).padStart(2, "0");
  useEffect(() => {
    if (showModal) {
      const autoSubmit = setTimeout(() => {
        routeScore();
      }, 4000);

      return () => clearTimeout(autoSubmit);
    }
  }, [showModal]);
  const routeScore = async () => {
    try {
      const API_URL =
        process.env.REACT_APP_API_URL + "/attempts/" + id + "/status";

      const body = { status: "Submitted" };

      const config = {
        headers: { "ngrok-skip-browser-warning": "true" },
      };

      const { data } = await axios.put(API_URL, body, config);

      if (data.success) {
        navigate(`/score/${id}/${tempCode}`, {
          state: { total: total },
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const getQuestion = async (page, loading) => {
    const params = {
      attempt_id: id,
      page,
      per_page: perPage,
    };

    const config = {
      headers: { "ngrok-skip-browser-warning": "true" },
      params,
    };

    const API_URL = process.env.REACT_APP_API_URL + "/questions";

    try {
      setIsLoading(loading);

      const response = await axios.get(API_URL, config);

      const payload = response.data.payload[0];

      setTotal(response.data.total);

      const questionOptions = {
        question: {
          id: payload.question_id,
          question: payload.question,
        },
        options: payload.options,
        attempted: payload.options.some((o) => o.selected === true),
        page,
      };

      const updatedQuestions = [...questions];

      const existingIndex = updatedQuestions.findIndex((q) => q.page === page);

      if (existingIndex !== -1)
        updatedQuestions[existingIndex] = questionOptions;
      else updatedQuestions.push(questionOptions);

      setQuestions(updatedQuestions);

      setCurrentPageQuestions(questionOptions);

      if (page > currentPage) {
        setQuestionNumber((n) => n + 1);
      } else if (page < currentPage) {
        setQuestionNumber((n) => n - 1);
      }

      setCurrentPage(page);

      setTotalQuestions(response.data.total);

      setIsLoading(false);
    } catch (err) {
      console.error(err);
      setIsLoading(false);
    }
  };

  const saveSelectedOption = async (questionId, optionId) => {
    if (isSelecting || showModal) return;

    setIsSelecting(true);

    const API_URL = process.env.REACT_APP_API_URL + "/select_option";

    try {
      await axios.post(API_URL, {
        attempt_answer_option_id: optionId,
      });

      await getQuestion(currentPage, false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSelecting(false);
    }
  };

  const areAllAttempted = (page) =>
    questions.every((q) => q.page !== page || q.attempted);

  const showNext =
    currentPage !== totalQuestions / perPage && areAllAttempted(currentPage);

  const showSubmit =
    currentPage === totalQuestions / perPage && areAllAttempted(currentPage);

  const showPrev = currentPage !== 1;

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex justify-center items-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div
        style={{
          pointerEvents: showModal ? "none" : "auto",
          userSelect: showModal ? "none" : "auto",
          overflow: showModal ? "hidden" : "auto",
        }}
      >
        <div style={styles.header}>
          <img src={logo} alt="GoApptiv" style={styles.logo} />

          <div style={styles.timerBox}>
            <h2 style={styles.timerText}>
              ⏳ Time Left : {minutes}:{seconds}
            </h2>
          </div>
        </div>

        <div style={styles.container}>
          <div style={styles.titleRow}>
            <div style={styles.titleWrapper}>
              <h1 style={styles.title}>Multiple Choice Questions</h1>

              <div style={styles.titleUnderline} />
            </div>

            <span style={styles.answeredBadge}>
              {questionNumber - 1}/{totalQuestions} Answered
            </span>
          </div>

          <div style={styles.card}>
            <QuestionOption
              question={currentPageQuestions.question}
              options={currentPageQuestions.options}
              total={totalQuestions}
              current={questionNumber}
              onClick={saveSelectedOption}
              isSelecting={isSelecting}
            />
          </div>

          <div style={styles.navRow}>
            {showPrev ? (
              <button
                style={styles.prevBtn}
                onClick={() => getQuestion(currentPage - 1, true)}
              >
                ← Prev
              </button>
            ) : (
              <div />
            )}

            <div style={styles.rightButtons}>
              {showSubmit && (
                <button style={styles.nextBtn} onClick={routeScore}>
                  Submit →
                </button>
              )}

              {!showSubmit && (
                <button
                  style={{
                    ...styles.nextBtn,
                    ...(isSelecting || !showNext ? styles.disabledBtn : {}),
                  }}
                  onClick={() => getQuestion(currentPage + 1, true)}
                  disabled={isSelecting || !showNext}
                >
                  Save & Next →
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalBox}>
            <div style={styles.modalIcon}>⏳</div>

            <h2 style={styles.modalTitle}>Time's Up!</h2>

            <p style={styles.modalText}>Your assessment time has ended.</p>

            <div className="progress-bar-container">
              <div className="progress-bar-fill" />
            </div>

            <button style={styles.modalButton} onClick={routeScore}>
              Submit Assessment
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
    fontFamily: "'Segoe UI', sans-serif",
    display: "flex",
    flexDirection: "column",
    overflowX: "hidden",
    position: "relative",
  },

  header: {
    padding: "16px 24px",
    backgroundColor: "#fff",
    borderBottom: "1px solid #eee",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  logo: {
    height: "clamp(28px, 4vw, 40px)",
    width: "auto",
    objectFit: "contain",
  },

  timerBox: {
    backgroundColor: "#fff3e0",
    padding: "10px 18px",
    borderRadius: "10px",
    border: "1px solid #ffd59e",
  },

  timerText: {
    margin: 0,
    fontSize: "16px",
    fontWeight: "700",
    color: "#F5A623",
  },

  container: {
    flex: 1,
    width: "100%",
    maxWidth: "1400px",
    margin: "0 auto",
    padding: "24px clamp(16px, 4vw, 40px)",
    boxSizing: "border-box",
  },

  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    flexWrap: "wrap",
    marginBottom: 24,
  },

  titleWrapper: {
    flex: 1,
    minWidth: "220px",
  },

  title: {
    fontSize: "clamp(20px, 3vw, 30px)",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
    lineHeight: 1.3,
  },

  titleUnderline: {
    height: 3,
    backgroundColor: "#F5A623",
    borderRadius: 2,
    marginTop: 8,
    width: "100%",
  },

  answeredBadge: {
    fontSize: "clamp(12px, 2vw, 15px)",
    color: "#555",
    fontWeight: 500,
    whiteSpace: "nowrap",
    paddingTop: 4,
  },

  card: {
    backgroundColor: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: 12,
    padding: "clamp(16px, 3vw, 32px)",
    width: "100%",
    boxSizing: "border-box",
    overflow: "hidden",
  },

  navRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
    marginTop: 28,
  },

  rightButtons: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },

  prevBtn: {
    padding: "12px clamp(16px, 2vw, 24px)",
    borderRadius: 8,
    border: "1px solid #ccc",
    backgroundColor: "#fff",
    color: "#333",
    fontSize: "clamp(13px, 2vw, 15px)",
    fontWeight: 500,
    cursor: "pointer",
    minWidth: "110px",
  },

  nextBtn: {
    padding: "12px clamp(16px, 2vw, 24px)",
    borderRadius: 8,
    border: "none",
    backgroundColor: "#F5A623",
    color: "#fff",
    fontSize: "clamp(13px, 2vw, 15px)",
    fontWeight: 600,
    cursor: "pointer",
    minWidth: "140px",
  },

  disabledBtn: {
    backgroundColor: "#BDBDBD",
    cursor: "not-allowed",
    opacity: 0.7,
  },

  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.55)",
    backdropFilter: "blur(4px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },

  modalBox: {
    width: "90%",
    maxWidth: "420px",
    backgroundColor: "#fff",
    borderRadius: "22px",
    padding: "36px 28px",
    textAlign: "center",
    boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
  },

  modalIcon: {
    fontSize: "52px",
    marginBottom: "14px",
  },

  modalTitle: {
    margin: 0,
    fontSize: "30px",
    fontWeight: "700",
    color: "#222",
  },

  modalText: {
    marginTop: "14px",
    fontSize: "16px",
    color: "#666",
    lineHeight: 1.6,
    marginBottom: "30px",
  },

  modalButton: {
    width: "100%",
    backgroundColor: "#F5A623",
    color: "#fff",
    border: "none",
    padding: "14px 20px",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: "700",
    cursor: "pointer",
  },
};

export default Question;
